import React, { useEffect } from 'react';
import { unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PortalContext } from './context/portal-context';
import { PageTrackingProvider } from './context/page-tracking-context';
import { UpdatingProvider } from './context/updating-context';
import { configProviderManager } from './fetch-bff';
import { useUserData } from './hooks/use-user-data';
import { LoadingManager } from './components/loading-manager/loading-manager';
import {
	NotFoundRedirect,
	PublicOnly,
	RequireAuth,
	RequireClubMember,
	RequireOnboardingStep,
	RequireVoucher,
	UnexpectedErrorOnly,
	CampaignRedirector,
} from './routes/protected-routes';
import {
	LoginPage,
	UnexpectedErrorPage,
	SignUpPage,
	PendingPage,
	AcceptancePage,
	NoVehiclesPage,
	AddVehiclePage,
	AddVehiclePendingPage,
	DashboardPage,
	SalesforceVoucherPage,
} from './pages';

export function App(): JSX.Element {
	const {
		featureAppId,
		authService,
		profileService,
		userService,
		layerManager,
		serviceConfigProvider,
		historyService,
		portalService,
	} = React.useContext(PortalContext);

	const {
		userData,
		userVehicles,
		personBenefits,
		dataCollection,
		isUpdating,
		hasError,
		hasToLog,
		validatingIDKit,
		setIsUpdating,
		vehicleToBeCreated,
		isLoading,
		normalizedVouchers,
	} = useUserData();

	console.log('🚀 ~ App ~ featureAppId', featureAppId);
	console.log('🚀 ~ App ~ authService', authService);
	console.log('🚀 ~ App ~ profileService', profileService);
	console.log('🚀 ~ App ~ userService', userService);
	console.log('🚀 ~ App ~ layerManager', layerManager);
	console.log('🚀 ~ App ~ serviceConfigProvider', serviceConfigProvider);
	console.log('🚀 ~ App ~ portalService', portalService);
	configProviderManager.setConfigs(serviceConfigProvider?.configs as any);

	// patrón adaptador -> adapta historyService.history al formato esperado por HistoryRouter de React Router v6
	const routerHistory = React.useMemo(
		() => ({
			get action() {
				return historyService.history.action;
			},
			get location() {
				return historyService.history.location;
			},
			push: historyService.history.push,
			replace: historyService.history.replace,
			go: historyService.history.go,
			createHref: historyService.history.createHref,
			listen: (listener: (update: { action: unknown; location: unknown }) => void) =>
				historyService.history.listen((location, action) => listener({ location, action })),
		}),
		[historyService],
	);

	// solo mostramos LoadingManager si realmente está cargando Y NO es un estado explícito de login/logout
	const showLoader = (isLoading || isUpdating) && !hasToLog;

	// listener para detectar cuando el usuario acaba de cerrar sesión
	useEffect(() => {
		authService.subscribe('logout', async () => {
			sessionStorage.setItem('userHasJustLoggedOut', 'true');
		});
	}, [authService]);

	return (
		<HistoryRouter history={routerHistory as any}>
			<UpdatingProvider setUpdating={setIsUpdating}>
				<PageTrackingProvider>
					{hasError || showLoader ? (
						<LoadingManager
							userData={userData}
							userVehicles={userVehicles}
							personBenefits={personBenefits}
							hasError={hasError}
							validatingIDKit={validatingIDKit}
							isLoading={isLoading}
						/>
					) : (
						<Routes>
							{/* RUTAS PÚBLICAS */}
							{/* <Route path="subpage" element={<Subpage />} /> */}

							{/* RUTAS SOLO PARA USUARIOS NO AUTENTICADOS - redirige a la ruta esperada si ya está logueado */}
							<Route element={<PublicOnly userData={userData} hasToLog={hasToLog} userVehicles={userVehicles} />}>
								<Route path="login" element={<LoginPage />} />
							</Route>

							{/* RUTAS QUE REQUIEREN AUTENTICACIÓN BASE */}
							<Route element={<RequireAuth userData={userData} hasToLog={hasToLog} />}>
								{/* RUTA DE ERROR INESPERADO */}
								<Route element={<UnexpectedErrorOnly userData={userData} userVehicles={userVehicles} />}>
									<Route path="unexpected-error" element={<UnexpectedErrorPage />} />
								</Route>

								{/* RUTAS EXCLUSIVAS PARA NO MIEMBROS DEL CLUB QUE ESTÁN EN PROCESO DE REGISTRO */}
								<Route element={<RequireOnboardingStep userData={userData} userVehicles={userVehicles} />}>
									<Route path="sign-up" element={<SignUpPage />} />
									<Route path="pending" element={<PendingPage />} />
									<Route path="acceptance" element={<AcceptancePage />} />
									<Route path="no-vehicles" element={<NoVehiclesPage vehicles={userVehicles?.vehicles ?? []} />} />
									<Route path="add-vehicle" element={<AddVehiclePage registerToClub={true} />} />
									<Route path="add-vehicle/pending" element={<AddVehiclePendingPage vehicle={vehicleToBeCreated} />} />
								</Route>

								{/* RUTAS EXCLUSIVAS PARA MIEMBROS DEL CLUB */}
								<Route element={<RequireClubMember userData={userData} userVehicles={userVehicles} />}>
									<Route index element={<Navigate to="/dashboard" replace />} />
									<Route
										path="dashboard"
										element={
											<DashboardPage
												personBenefits={personBenefits}
												userVehicles={userVehicles}
												userData={userData}
												dataCollection={dataCollection}
											/>
										}
									/>

									<Route element={<RequireVoucher normalizedVouchers={normalizedVouchers} />}>
										<Route
											path="voucher/salesforce/:id"
											element={<SalesforceVoucherPage normalizedVouchers={normalizedVouchers!} />}
										/>
									</Route>

									<Route
										path="voucher/salesforce/campaign/:id"
										element={<CampaignRedirector normalizedVouchers={normalizedVouchers!} />}
									/>
								</Route>

								{/* RUTA COMODÍN PARA RUTAS NO ENCONTRADAS */}
								<Route path="*" element={<NotFoundRedirect userData={userData} userVehicles={userVehicles} />} />
							</Route>
						</Routes>
					)}
				</PageTrackingProvider>
			</UpdatingProvider>
		</HistoryRouter>
	);
}
